import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {FormWrapper} from "../items/form.wrapper";
import {FormContactConfig} from "./form-contact-config";
import {AbstractFormFieldComponent} from "../abstract-form-field.component";
import {FormSelectOption} from "../items/form-select-option";

import {FormTypeaheadComponent} from "../form-typeahead/form-typeahead.component";
import {map, take} from "rxjs/operators";
import {ContactService} from "../../../services/contact.service";
import {ButtonComponent} from "../../button/button.component";
import {IconComponent} from "../../icon/icon.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {TranslatePipe} from "@ngx-translate/core";
import {Paths} from "../../../paths";
import {isDefined, isUuid, notDefined, unsubscribe} from "../../utils/epic.utils";
import {interval, Observable, Subscription} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {Permissions} from "../../../services/auth/permissions.item";
import {ContactCategory} from "../../enum/contact-category.enum";
import {ContactType} from "../../enum/contact-type";
import {SearchContactRes} from "../../../repositories/dto/search/search-contact.res";
import {FormSelectComponent} from "../form-select/form-select.component";
import {Additionals} from "../../../repositories/item/additionals";
import {FormTypeaheadConfig} from "../form-typeahead/form-typeahead.config";
import {Size} from "../../../base/data/size";
import {FormMetadata} from "../items/form-metadata";

interface QueryParams {
  id: string;
  type?: ContactType;
  category?: ContactCategory;
}

@Component({
  selector: 'acc-form-contact',
  imports: [
    FormTypeaheadComponent,
    ButtonComponent,
    IconComponent,
    NgbTooltip,
    TranslatePipe,
    FormSelectComponent
  ],
  templateUrl: './form-contact.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-contact.component.css'
})
export class FormContactComponent extends AbstractFormFieldComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @ViewChild('typeahead')
  public typeahead!: FormTypeaheadComponent;

  @Input({required: true})
  public name!: string;

  @Input()
  public nameEmployeeType: string | undefined = undefined;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input()
  public config: FormContactConfig = new FormContactConfig();

  @Input()
  public waitForContactId: string | undefined;

  // Can be used when other elements are displayed to align horizontally, e.g. “d-flex gap-2”
  @Input()
  public accClass: string | undefined;

  @Output()
  public accChanged: EventEmitter<FormSelectOption | undefined> = new EventEmitter<FormSelectOption | undefined>();

  public static readonly URL_PARAM: string = 'formContact';

  protected readonly typeaheadConfig: FormTypeaheadConfig = new FormTypeaheadConfig();
  protected perms: Permissions;

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly contactService: ContactService = inject(ContactService);

  private readonly subs: Subscription[] = [];
  private waitForContactIdSub: Subscription | undefined;

  constructor() {
    super();
    this.perms = this.permissionService.getPerms();
  }

  ngOnInit(): void {
    this.subs.push(
      this.permissionService.accChanged().subscribe((p) => this.perms = p),
      this.wrapper.onReady().subscribe(() => this.initForm()),
      this.accChanged.subscribe((o) => this.loadEmployeeType(o)),
    );
    this.typeaheadConfig.size = this.config.size;
  }

  ngAfterViewInit(): void {
    this.subs.push(this.wrapper.onReady().subscribe(() => this.loadContactIdFromUrl()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Delayed set of the “Selection” contacts.
    // This can happen when the contact was created directly and does not yet exist in the ES,
    // E.g., when uploading the receipt and a new contact is created
    if (isDefined(changes['waitForContactId']) && isUuid(changes['waitForContactId'].currentValue)) {
      const contactId: string = changes['waitForContactId'].currentValue;
      unsubscribe(this.waitForContactIdSub);
      this.waitForContactIdSub = interval(1000).pipe(take(5)).subscribe(() => {
        const value: string | undefined = this.typeahead.getCurrentValueAsString();
        if (notDefined(value) || value !== contactId) {
          this.setInitValue();
        } else {
          unsubscribe(this.waitForContactIdSub);
        }
      });
    }
    if (isDefined(changes['config']) && this.wrapper.isReady && isDefined(this.typeahead)) {
      this.setInitValue();
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
    unsubscribe(this.waitForContactIdSub);
  }

  public setInitValue(): void {
    this.typeahead.setInitValue().then((v) => this.loadEmployeeType(v));
  }

  // noinspection JSUnusedGlobalSymbols
  public cleanValue(): void {
    this.typeahead.cleanValue();
  }

  public allowAddContact(): boolean {
    return this.config.addNewContact && this.perms.contact.write && !this.isContact() && !this.isViewOnly();
  }

  protected addNewContact(): void {
    const url: string = this.urlService.replaceOrExtendInUrl(';' + FormContactComponent.URL_PARAM + '=', ';' + FormContactComponent.URL_PARAM + '={contactId}');
    const params: QueryParams = {id: 'new'};
    if (isDefined(this.config.addNewContactType)) {
      params.type = this.config.addNewContactType;
    }
    if (isDefined(this.config.addNewContactCategory)) {
      params.category = this.config.addNewContactCategory;
    }
    this.urlService.navigateWithBackLink(Paths.CONTACT_EDIT, params, url);
  }

  private initForm(): void {
    const metadata: FormMetadata | undefined = this.wrapper.getMetadataByName(this.name);
    if (isDefined(metadata)) {
      metadata.setAsyncSelectOption((input) => {
        return this.getTypeaheadData(input).pipe(
          map((res) => res.items),
          map((items) => items.map((item) => {
            const name: string = item.number + ' ' + item.fullName;
            const icon: string = 'contact-' + item.type.toLowerCase();
            return new FormSelectOption(name, item.id).useName().setIcon(icon);
          }))
        );
      });
      metadata.setAsyncSelectOptionById((id) => this.getContactByIdAsOption(id));
      this.loadEmployeeType();
    } else {
      this.log.error('Metadata not found for "{}"', [this.name]);
    }
  }

  private getTypeaheadData(input: any): Observable<SearchContactRes> {
    const category: ContactCategory | undefined = isDefined(this.config.category) ? this.config.category : this.config.filterCategory;
    const type: ContactType | undefined = this.config.filterType;
    return this.contactService.typeahead(input, category, type);
  }

  // Explicitly resolves the selected contact by id, so it is loaded even when it is
  // not part of the limited typeahead result list (e.g. far down in a large contact list).
  private getContactByIdAsOption(id: string): Observable<FormSelectOption | undefined> {
    return this.contactService.getById(id, Additionals.NONE).pipe(
      map((c) => {
        if (notDefined(c) || !isUuid(c.id)) {
          return undefined;
        }
        const name: string = c.number + ' ' + c.fullName;
        const icon: string = 'contact-' + c.type.toLowerCase();
        return new FormSelectOption(name, c.id).useName().setIcon(icon);
      })
    );
  }

  private loadContactIdFromUrl(): void {
    this.loadContactById(this.urlService.getValueFromRoute(this.route, FormContactComponent.URL_PARAM));
  }

  private loadContactById(contactId: string | undefined): void {
    if (isDefined(contactId) && isUuid(contactId) && this.wrapper.getValue(this.name) !== contactId) {
      this._onChangeValue(this.name, this.wrapper, contactId);
    }
  }

  protected override isViewOnly(): boolean {
    return this._isViewOnly(this.name, this.wrapper);
  }

  protected isContact(): boolean {
    return isUuid(this.getContactId());
  }

  protected goToContact(): void {
    this.urlService.navigate([Paths.CONTACT_VIEW, {id: this.getContactId()}]).then();
  }

  private getContactId(): string | undefined {
    if (this.wrapper.hasControl(this.name)) {
      return this.wrapper.getValue(this.name);
    }
    return undefined;
  }

  // E.g., receipt case - business trip
  private loadEmployeeType(option?: string | FormSelectOption): void {
    if (this.config.employeeTypeSelectShow && isDefined(this.nameEmployeeType)
      && this.wrapper.isReady && this.wrapper.hasControl(this.nameEmployeeType)) {
      let contactId: string | undefined = undefined;
      if (isDefined(option) && option instanceof FormSelectOption && isUuid(option.value)) {
        contactId = option.value;
      } else if (notDefined(option) && isUuid(this.wrapper.getValue(this.name))) {
        contactId = this.wrapper.getValue(this.name);
      }

      if (isUuid(contactId)) {
        this.subs.push(this.contactService.getById(contactId, Additionals.NONE).subscribe((c) => {
          if (this.isViewOnly()) {
            this.wrapper.patchValue(this.nameEmployeeType!, c.employeeType);
          } else {
            // Form mark field as dirty
            this.wrapper.setValue(this.nameEmployeeType!, c.employeeType);
          }
          if (!this.config.employeeTypeSelectDisabled && !this.isViewOnly()) {
            this.wrapper.getControl(this.nameEmployeeType!).enable();
          }
        }));
      } else {
        this.wrapper.setValue(this.nameEmployeeType!, '');
        this.wrapper.getControl(this.nameEmployeeType!).disable();
      }

      if (this.config.employeeTypeSelectDisabled && !this.isViewOnly()) {
        this.wrapper.getControl(this.nameEmployeeType!).disable();
      }
    }
  }

  protected getAddButtonClass(): string {
    if (this.config.size === Size.SMALL) {
      return '';
    }
    return 'form-group';
  }
}
